
/**
 * Module dependencies.
 */

import { GoogleMap, GoogleMapProps, LoadScript } from '@react-google-maps/api';
import { mapStyle } from 'src/components/core/maps/map-style';
import { mapUrlResolver } from 'src/core/utils/maps';
import { useRouter } from 'next/router';
import { useTranslate } from 'src/core/utils/translator';
import Marker from './marker';
import React, { ReactElement, useCallback } from 'react';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';

/**
 * Api key.
 */

const apiKey: string = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;

/**
 * `MarkerProps` type.
 */

type MarkerProps = {
  coordinates: google.maps.LatLngLiteral[],
  id: string,
  name?: string | null
};

/**
 * `Props` type.
 */

type Props = GoogleMapProps & {
  center?: google.maps.LatLngLiteral | undefined,
  fitBounds?: boolean,
  isInView?: boolean,
  markers: MarkerProps[] | null | undefined,
  maxZoom?: number,
  zoom?: number
};

/**
 * `Map` component.
 */

const Map = (props: Props): ReactElement | null => {
  const { fitBounds, isInView, markers, maxZoom, ...rest } = props;
  const zoom = props.zoom ?? maxZoom;
  const { locale } = useRouter();
  const { translate } = useTranslate();
  const handleLoad = useCallback(map => {
    if (fitBounds && !isEmpty(markers)) {
      const bounds = new window.google.maps.LatLngBounds();

      forEach(markers, ({ coordinates }) => {
        bounds.extend(coordinates);
      });

      map.fitBounds(bounds);
    }

    const mapDiv = map.getDiv();

    const addTableCaption = () => {
      const table = mapDiv.querySelector('table');

      if (table && !table.getAttribute('role')) {
        table.setAttribute('role', 'presentation');

        const caption = document.createElement('caption');

        caption.textContent = translate('common:labels.mapKeyboardShortcuts');
        caption.style.cssText = 'position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0;';
        table.insertBefore(caption, table.firstChild);

        return true;
      }

      return false;
    };

    if (!addTableCaption()) {
      const observer = new MutationObserver(() => {
        if (addTableCaption()) {
          observer.disconnect();
        }
      });

      observer.observe(mapDiv, { childList: true, subtree: true });
    }
  }, [fitBounds, markers, translate]);

  if (!apiKey && !isInView) {
    return null;
  }

  return (
    <LoadScript
      googleMapsApiKey={apiKey}
      language={locale}
    >
      <GoogleMap
        mapContainerStyle={{
          height: '100%',
          width: '100%'
        }}
        onLoad={handleLoad}
        options={{
          styles: mapStyle as google.maps.MapTypeStyle[]
        }}
        {...rest}
        zoom={zoom}
      >
        {!isEmpty(markers) && map(markers, ({ coordinates, id, name }) => (
          <Marker
            key={id}
            onClick={() => window.open(mapUrlResolver(coordinates))}
            position={{
              lat: coordinates.lat,
              lng: coordinates.lng
            }}
            title={name}
          />
        ))}
      </GoogleMap>
    </LoadScript>
  );
};

/**
 * Default props.
 */

Map.defaultProps = {
  maxZoom: 10
};

/**
 * Export `Map` component.
 */

export default Map;
